// src/pages/PatientDoctorsPage.jsx
// Página pública para que el paciente busque médicos (M3 + filtros + agrupado por país + WhatsApp)
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  TextField,
  Chip,
  Stack,
  Avatar,
  CircularProgress,
  InputAdornment,
  Divider,
  Rating,
  Skeleton,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PlaceIcon from '@mui/icons-material/Place';
import EmailIcon from '@mui/icons-material/Email';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import GradeIcon from '@mui/icons-material/Grade';
import PhoneIcon from '@mui/icons-material/LocalPhone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axiosInstance from '../axiosInstance';

// Lista de especialidades (proporcionada)
const SPECIALTIES = [
  'Medicina Interna','Cardiología','Endocrinología','Gastroenterología','Hematología',
  'Nefrología','Neumología','Neurología','Oncología','Reumatología',
  'Terapia Intensiva (Medicina Crítica)',
  'Cirugía General','Cirugía Cardiotorácica','Cirugía Pediátrica','Cirugía Plástica',
  'Cirugía Vascular','Neurocirugía','Ortopedia y Traumatología','Urología',
  'Administración Hospitalaria','Alergología e Inmunología','Algología','Anestesiología','Audiología',
  'Bioestadística Aplicada','Biotecnología Médica','Cuidados Paliativos','Dermatología','Epidemiología',
  'Farmacología Clínica','Fisiología Humana','Genética Médica','Geriatría',
  'Ginecología y Obstetricia','Infectología','Kinesiología',
  'Maestría en Docencia Universitaria en Ciencias de la Salud',
  'Maestría en Economía de la Salud',
  'Maestría en Innovación en Salud Digital',
  'Maestría en Inteligencia Artificial Aplicada a la Medicina',
  'Maestría en Gestión Sanitaria y Calidad en Salud',
  'Maestría en Políticas Públicas de Salud',
  'Maestría en Telemedicina y eSalud',
  'Medicina Aeroespacial','Medicina de Desastres y Catástrofes',
  'Medicina de Emergencia','Medicina de Transporte y Evacuación Médica',
  'Medicina de Urgencias Pediátricas',
  'Medicina del Deporte','Medicina del Dolor Intervencionista','Medicina del Sueño',
  'Medicina del Trabajo','Medicina Estética','Medicina Familiar',
  'Medicina Física y Rehabilitación','Medicina Forense','Medicina General',
  'Medicina Hiperbárica','Medicina Nuclear','Medicina Preventiva','Medicina Transfusional',
  'Medicina Tropical','Neurociencias','Neurofisiología Clínica',
  'Nutriología Clínica','Oftalmología','Otorrinolaringología',
  'Patología','Pediatría','Psiquiatría','Radiología',
  'Salud Pública','Terapia Física y Rehabilitación','Toxicología Clínica',
];

// Monedas
const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', MXN: '$', HNL: 'L', GTQ: 'Q', CRC: '₡', NIO: 'C$',
  PAB: 'B/.', DOP: 'RD$', ARS: '$', CLP: '$', COP: '$', PEN: 'S/',
  BOB: 'Bs', BRL: 'R$', UYU: '$U', PYG: '₲', VES: 'Bs.', GBP: '£',
};
const getCurrencySymbol = (code) => CURRENCY_SYMBOLS[(code || '').toUpperCase()] || '';
const formatMoney = (amount, code) => {
  const sym = getCurrencySymbol(code);
  const val = Number(amount ?? 0);
  const formatted = (Number.isFinite(val) ? (val % 1 === 0 ? val.toFixed(0) : val.toFixed(2)) : '0');
  return `${sym ? sym + ' ' : ''}${formatted} ${code || ''}`.trim();
};

// Claves posibles para email/telefono/código de país en endpoints
const EMAIL_KEYS = [
  'email','email_consultorio','correo','correo_contacto','correo_consultorio',
  'contact_email','email_publico','email_contacto','usuario_email',
];
const PHONE_KEYS = [
  'telefono_consultorio','telefono','telefono_principal','phone','phone_number','telefono_contacto',
];
const CODE_KEYS = [
  'codigo_pais_consultorio','codigo_pais','country_code',
];

// Endpoints candidatos (en orden). Puedes dejar solo '/patients/doctors/' si ya acepta invitados.
const LIST_CANDIDATES = [
  '/patients/doctors/',   // ViewSet del portal (con permiso guest)
  '/doctor-list/',        // función pública (si la mapeaste en urls)
];

const PatientDoctorsPage = () => {
  const theme = useTheme();
  const { doctorId: doctorIdFromParams } = useParams();
  const navigate = useNavigate();
  const isGuest = localStorage.getItem('isGuest') === 'true';
  const patientToken = localStorage.getItem('patientToken');

  const handleBook = (doctorId) => {
    if (patientToken) {
      navigate(`/patient/book/${doctorId}`);
    } else if (isGuest) {
      navigate(`/book/${doctorId}`);
    } else {
      navigate(`/book/${doctorId}`);
    }
  };

  // Datos
  const [loading, setLoading] = useState(true);
  const [doctors, setDoctors] = useState([]);
  const [fetchError, setFetchError] = useState('');

  // Filtros
  const [search, setSearch] = useState('');
  const [selectedSpec, setSelectedSpec] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');

  // Caches
  const [ratingsMap, setRatingsMap] = useState({});
  const [detailsMap, setDetailsMap] = useState({});
  const ratingsFetchedRef = useRef(new Set());
  const detailsFetchedRef = useRef(new Set());

  // Helper: intenta múltiples endpoints hasta obtener 200
  const fetchDoctorsFromAny = async (params = {}) => {
    setFetchError('');
    const candidates = LIST_CANDIDATES; // <- no filtrar por patientToken

    let lastErr = null;
    for (const path of candidates) {
      try {
        const res = await axiosInstance.get(path, { params });
        if (Array.isArray(res.data)) return res.data;
        if (res.data && Array.isArray(res.data.results)) return res.data.results;
      } catch (e) {
        lastErr = e;
        // Seguimos probando con el siguiente endpoint
      }
    }
    throw lastErr || new Error('No hay endpoint público para listado de médicos.');
  };

  // Carga inicial
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const raw = await fetchDoctorsFromAny({});
        if (mounted) setDoctors(raw);
      } catch (e) {
        if (mounted) {
          setDoctors([]);
          setFetchError('No se pudo cargar el listado de médicos.');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Búsqueda con debounce (300ms)
  useEffect(() => {
    let active = true;
    const h = setTimeout(async () => {
      try {
        setLoading(true);
        const params = {};
        if (search) params.nombre = search;
        if (selectedSpec) params.especialidad = selectedSpec;
        if (selectedCountry) params.pais = selectedCountry;
        const raw = await fetchDoctorsFromAny(params);
        if (active) setDoctors(raw);
      } catch (e) {
        if (active) {
          setDoctors([]);
          setFetchError('No se pudo cargar el listado de médicos con los filtros seleccionados.');
        }
      } finally {
        if (active) setLoading(false);
      }
    }, 300);
    return () => { active = false; clearTimeout(h); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, selectedSpec, selectedCountry]);

  // Helpers de lectura
  const getSpec = (doc) =>
    (doc.especialidad || doc.especialidad_principal || 'Especialidad no disponible');

  const readFirstKey = (obj, keys) => {
    for (const k of keys) {
      const raw = obj?.[k];
      if (raw === null || typeof raw === 'undefined') continue;
      const v = String(raw).trim();
      if (v) return v;
    }
    return '';
  };

  const getEmail = (doc) => {
    const fromDoc = readFirstKey(doc, EMAIL_KEYS);
    if (fromDoc) return fromDoc;
    const d = detailsMap[doc.id_medico] || {};
    return readFirstKey(d, EMAIL_KEYS);
  };

  const getCountry = (doc) => {
    const fromDoc = (doc.pais || doc.country || '').toString().trim();
    if (fromDoc) return fromDoc;
    const d = detailsMap[doc.id_medico] || {};
    return (d.pais || '').toString().trim();
  };

  const getMoneda = (doc) => {
    const fromDoc = (doc.moneda || '').toString().trim().toUpperCase();
    if (fromDoc) return fromDoc;
    const d = detailsMap[doc.id_medico] || {};
    const fromDetails = (d.moneda || '').toString().trim().toUpperCase();
    return fromDetails || 'USD';
  };

  const getPriceLabel = (doc) => {
    if (typeof doc.tarifa_consulta === 'undefined' || doc.tarifa_consulta === null) return null;
    return formatMoney(doc.tarifa_consulta, getMoneda(doc));
  };

  const getPhoneInfo = (doc) => {
    const d = detailsMap[doc.id_medico] || {};
    const code = readFirstKey(doc, CODE_KEYS) || readFirstKey(d, CODE_KEYS);
    const phone = readFirstKey(doc, PHONE_KEYS) || readFirstKey(d, PHONE_KEYS);

    const label = [code, phone].filter(Boolean).join(' ').trim() || phone;
    const digits = (String(code || '') + ' ' + String(phone || ''))
      .replace(/\+/g, '')
      .replace(/[^\d]/g, '')
      .trim();

    const waNumber = digits || '';
    return { label: label || '', waNumber };
  };

  // Filtro local
  const filteredDoctors = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (doctors || []).filter(d => {
      const name = (d.nombre_completo || '').toLowerCase();
      const spec = getSpec(d).toLowerCase();
      const country = getCountry(d).toLowerCase();

      const passSearch = !q || name.includes(q);
      const passSpec = !selectedSpec || spec === selectedSpec.toLowerCase();
      const passCountry = !selectedCountry || country === selectedCountry.toLowerCase();
      return passSearch && passSpec && passCountry;
    });
  }, [doctors, search, selectedSpec, selectedCountry, detailsMap]);

  // Lista de países
  const countries = useMemo(() => {
    const set = new Set();
    (doctors || []).forEach(d => {
      const c = getCountry(d);
      if (c) set.add(c);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [doctors, detailsMap]);

  // Agrupar por país
  const groupedByCountry = useMemo(() => {
    const groups = {};
    (filteredDoctors || []).forEach(doc => {
      const key = getCountry(doc) || 'Sin país';
      if (!groups[key]) groups[key] = [];
      groups[key].push(doc);
    });
    return Object.keys(groups).sort((a, b) => a.localeCompare(b)).map(country => ({
      country, items: groups[country]
    }));
  }, [filteredDoctors, detailsMap]);

  // Cargar ratings summary
  useEffect(() => {
    const ids = (filteredDoctors || []).map(d => d.id_medico).filter(Boolean);
    const toFetch = ids.filter(id => !ratingsFetchedRef.current.has(id));
    if (!toFetch.length) return;

    let canceled = false;
    (async () => {
      try {
        const tasks = toFetch.map(async (id) => {
          ratingsFetchedRef.current.add(id);
          try {
            const { data } = await axiosInstance.get(`/doctors/${id}/reviews/summary/`);
            if (!canceled) {
              setRatingsMap(prev => ({
                ...prev,
                [id]: { average: Number(data?.average || 0), total: Number(data?.total || 0) }
              }));
            }
          } catch {
            if (!canceled) {
              setRatingsMap(prev => ({ ...prev, [id]: { average: 0, total: 0 } }));
            }
          }
        });
        await Promise.all(tasks);
      } catch {
        // noop
      }
    })();

    return () => { canceled = true; };
  }, [filteredDoctors]);

  // Cargar detalles públicos
  useEffect(() => {
    const ids = (filteredDoctors || []).map(d => d.id_medico).filter(Boolean);
    const toFetch = ids.filter(id => !detailsFetchedRef.current.has(id));
    if (!toFetch.length) return;

    let canceled = false;
    (async () => {
      try {
        const tasks = toFetch.map(async (id) => {
          detailsFetchedRef.current.add(id);
          try {
            const { data } = await axiosInstance.get(`/doctors/${id}/public/`);
            if (canceled) return;

            const emailResolved = readFirstKey(data, EMAIL_KEYS);
            const phoneResolved = readFirstKey(data, PHONE_KEYS);
            const codeResolved = readFirstKey(data, CODE_KEYS);

            setDetailsMap(prev => ({
              ...prev,
              [id]: {
                pais: (data?.pais || data?.country || '').toString().trim(),
                moneda: (data?.moneda || '').toString().trim().toUpperCase() || undefined,
                email: emailResolved,
                telefono: phoneResolved,
                codigo_pais: codeResolved,
              }
            }));
          } catch {
            // noop
          }
        });
        await Promise.all(tasks);
      } catch {
        // noop
      }
    })();

    return () => { canceled = true; };
  }, [filteredDoctors]);

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      {/* Título */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>
          <LocalHospitalIcon />
        </Avatar>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Buscar Médicos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Encuentra especialistas por nombre, especialidad y país
          </Typography>
        </Box>
      </Stack>

      {/* Filtros */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Buscar por nombre"
            placeholder="Ej: Juan Pérez"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Chip
              label="Todas las especialidades"
              color={selectedSpec ? 'default' : 'primary'}
              onClick={() => setSelectedSpec('')}
              variant={selectedSpec ? 'outlined' : 'filled'}
            />
            {SPECIALTIES.map((spec) => (
              <Chip
                key={spec}
                label={spec}
                color={selectedSpec === spec ? 'primary' : 'default'}
                onClick={() => setSelectedSpec(spec)}
                variant={selectedSpec === spec ? 'filled' : 'outlined'}
                sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) } }}
              />
            ))}
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            Filtrar por país
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Chip
              icon={<PlaceIcon />}
              label="Todos los países"
              color={selectedCountry ? 'default' : 'primary'}
              onClick={() => setSelectedCountry('')}
              variant={selectedCountry ? 'outlined' : 'filled'}
            />
            {countries.map((c) => (
              <Chip
                key={c}
                icon={<PlaceIcon />}
                label={c}
                color={selectedCountry === c ? 'primary' : 'default'}
                onClick={() => setSelectedCountry(c)}
                variant={selectedCountry === c ? 'filled' : 'outlined'}
                sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) } }}
              />
            ))}
          </Stack>
        </Grid>
      </Grid>

      <Divider sx={{ mb: 2 }} />

      {!!fetchError && !loading && (
        <Typography color="error" sx={{ mb: 2 }}>{fetchError}</Typography>
      )}

      {/* Contenido */}
      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 240 }}>
          <CircularProgress />
        </Box>
      ) : filteredDoctors.length === 0 ? (
        <Typography color="text.secondary">No se encontraron médicos.</Typography>
      ) : (
        <Box>
          {groupedByCountry.map(({ country, items }) => (
            <Box key={country} sx={{ mb: 4 }}>
              <Divider textAlign="left" sx={{ mb: 2 }}>
                <Chip
                  icon={<PlaceIcon />}
                  label={country}
                  color="primary"
                  sx={{
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  }}
                />
              </Divider>

              <Grid container spacing={2}>
                {items.map((doc) => {
                  const rating = ratingsMap[doc.id_medico];
                  const price = getPriceLabel(doc);
                  const email = getEmail(doc);
                  const spec = getSpec(doc);
                  const countryLabel = getCountry(doc);
                  const phoneInfo = getPhoneInfo(doc);
                  const hasPhone = !!phoneInfo.label && !!phoneInfo.waNumber;

                  return (
                    <Grid key={doc.id_medico} item xs={12} sm={6} md={6}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          borderRadius: 3,
                          boxShadow: theme.shadows[1],
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: theme.shadows[6],
                            transform: 'translateY(-4px)',
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                          },
                        }}
                      >
                        <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                          {/* Cabecera */}
                          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                            <Avatar
                              src={doc.foto || doc.foto_perfil || undefined}
                              alt={doc.nombre_completo}
                              sx={{
                                width: 64,
                                height: 64,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                border: `3px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                              }}
                            >
                              <LocalHospitalIcon />
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="h6" fontWeight={700} noWrap title={doc.nombre_completo}>
                                {doc.nombre_completo}
                              </Typography>
                              <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 1 }}>
                                <Chip label={spec} size="small" color="primary" variant="outlined" sx={{ fontWeight: 700 }} />
                                {!!countryLabel && (
                                  <Chip icon={<PlaceIcon />} label={countryLabel} size="small" variant="outlined" />
                                )}
                                {!!price && (
                                  <Chip icon={<MonetizationOnIcon />} label={price} size="small" variant="outlined" />
                                )}
                              </Stack>
                            </Box>
                          </Stack>

                          {/* Rating */}
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            {rating ? (
                              <>
                                <Rating
                                  value={Number(rating.average) || 0}
                                  precision={0.1}
                                  readOnly
                                  sx={{
                                    color: theme.palette.warning.main,
                                    '& .MuiRating-iconFilled': { filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))' },
                                  }}
                                />
                                <Typography variant="body2" fontWeight={700}>
                                  {(Number(rating.average) || 0).toFixed(1)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  ({rating.total} opiniones)
                                </Typography>
                              </>
                            ) : (
                              <>
                                <Skeleton variant="rounded" width={120} height={24} />
                                <Skeleton variant="text" width={40} />
                              </>
                            )}
                          </Stack>

                          {/* Contacto */}
                          <Stack spacing={0.75} sx={{ mt: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <EmailIcon fontSize="small" color="disabled" />
                              <Typography variant="body2" color="text.secondary" noWrap title={email || 'No disponible'}>
                                {email ? (
                                  <a href={`mailto:${email}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                                    {email}
                                  </a>
                                ) : (
                                  'No disponible'
                                )}
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <PhoneIcon fontSize="small" color="disabled" />
                              <Typography variant="body2" color="text.secondary" noWrap title={phoneInfo.label || 'No disponible'}>
                                {phoneInfo.label || 'No disponible'}
                              </Typography>
                            </Stack>
                          </Stack>
                        </CardContent>

                        <CardActions sx={{ p: 2, pt: 0 }}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: '100%' }}>
                            {hasPhone && (
                              <Button
                                component="a"
                                href={`https://wa.me/${phoneInfo.waNumber}`}
                                target="_blank"
                                rel="noopener"
                                variant="outlined"
                                color="success"
                                startIcon={<WhatsAppIcon />}
                                sx={{
                                  flex: 1,
                                  borderRadius: '20px',
                                  py: 1.2,
                                  '&:hover': {
                                    boxShadow: `0 6px 16px ${alpha(theme.palette.success.main, 0.2)}`,
                                    transform: 'translateY(-1px)',
                                  },
                                }}
                              >
                                WhatsApp
                              </Button>
                            )}
                            <Button
                              fullWidth
                              variant="contained"
                              onClick={() => handleBook(doc.id_medico)}
                              startIcon={<GradeIcon />}
                              sx={{
                                flex: 1,
                                fontWeight: 700,
                                borderRadius: '20px',
                                py: 1.2,
                                boxShadow: 'none',
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.25)}`,
                                },
                              }}
                            >
                              Agendar
                            </Button>
                          </Stack>
                        </CardActions>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default PatientDoctorsPage;
